import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Menu, Button, Table, Modal, Form, Input, Select, message } from 'antd';
import Gstyle from './index.module.less';
import layoutStyles from './sharePageLayout.module.less';
import styles from './taTagManagement.module.less';
import useTableScrollY from './useTableScrollY';
import closeIcon from '@/asset/new/Close.png';
import DeleteIcon from '@/asset/new/Delete.png';
import editIcon from '@/asset/new/edit.png';
import dayjs from 'dayjs';
import {
    knowledgeDictionaryList,
    knowledgeTaTagList,
    knowledgeTaTagAdd,
    knowledgeTaTagUpdate,
    knowledgeTaTagDelete,
} from '@/api/knowledge';

const { Content } = Layout;

function normalizeDictRows(res) {
    if (!res || res.status !== 200 || res.data == null) {
        return [];
    }
    const raw = res.data;
    if (Array.isArray(raw)) {
        return raw;
    }
    return raw.records ?? raw.list ?? raw.rows ?? [];
}

function mapBuDictToTabs(list) {
    if (!Array.isArray(list)) {
        return [];
    }
    return list.map(item => ({
        label: item.dictLabel,
        value: item.dictValue,
    }));
}

function mapDictListToOptions(list) {
    if (!Array.isArray(list)) {
        return [];
    }
    return list.map(item => ({
        label: item.dictLabel,
        value: item.dictValue,
    }));
}

function isTagInUse(record) {
    const fileCnt = Number(record?.fileCnt ?? record?.fileTagCount ?? 0);
    const memberCnt = Number(record?.memberCnt ?? record?.memberTagCount ?? 0);
    return fileCnt > 0 || memberCnt > 0;
}

const TaTagManagement = () => {
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const [buTabs, setBuTabs] = useState([]);
    const [taTypeOptions, setTaTypeOptions] = useState([]);
    const [selectedBuId, setSelectedBuId] = useState(undefined);
    const [dataSource, setDataSource] = useState([]);
    const [total, setTotal] = useState(0);
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(false);
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [editModal, setEditModal] = useState({ visible: false, record: null });
    const [deleteModal, setDeleteModal] = useState({ visible: false, record: null });
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [errorModalContent, setErrorModalContent] = useState('');

    const { tableWrapRef, tableScrollY } = useTableScrollY([
        dataSource.length,
        loading,
        total,
        pageSize,
        selectedBuId,
    ]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [buRes, taTypeRes] = await Promise.all([
                    knowledgeDictionaryList({ dictType: 'BU' }),
                    knowledgeDictionaryList({ dictType: 'ta_type' }),
                ]);
                if (cancelled) {
                    return;
                }
                if (buRes.status === 200) {
                    const tabs = mapBuDictToTabs(normalizeDictRows(buRes));
                    setBuTabs(tabs);
                    if (tabs.length > 0) {
                        setSelectedBuId(prev =>
                            prev != null && tabs.some(t => String(t.value) === String(prev))
                                ? prev
                                : tabs[0].value,
                        );
                    } else {
                        setSelectedBuId(undefined);
                    }
                }
                if (taTypeRes.status === 200) {
                    setTaTypeOptions(mapDictListToOptions(normalizeDictRows(taTypeRes)));
                }
            } catch {
                if (!cancelled) {
                    setBuTabs([]);
                    setTaTypeOptions([]);
                    setSelectedBuId(undefined);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const fetchList = useCallback(async () => {
        if (selectedBuId == null || selectedBuId === '') {
            setDataSource([]);
            setTotal(0);
            return;
        }
        setLoading(true);
        try {
            const res = await knowledgeTaTagList({
                pageNo: current,
                pageSize,
                buId: selectedBuId,
            });
            if (res.status === 200 && res.data != null) {
                const data = res.data;
                const records = data.records ?? data.list ?? data.rows ?? [];
                setDataSource(Array.isArray(records) ? records : []);
                setTotal(data.total != null ? data.total : 0);
            } else {
                setDataSource([]);
                setTotal(0);
            }
        } catch (e) {
            message.error(e?.message || '获取列表失败');
            setDataSource([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [current, pageSize, selectedBuId]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const onBuTabChange = ({ key }) => {
        if (String(selectedBuId) === String(key)) {
            return;
        }
        setSelectedBuId(key);
        setCurrent(1);
    };

    const openAddModal = () => {
        if (selectedBuId == null || selectedBuId === '') {
            message.warning('请先选择 BU');
            return;
        }
        addForm.resetFields();
        setAddModalVisible(true);
    };

    const closeAddModal = () => {
        setAddModalVisible(false);
        addForm.resetFields();
    };

    const onConfirmAdd = async () => {
        try {
            const values = await addForm.validateFields();
            setLoading(true);
            const res = await knowledgeTaTagAdd({
                buId: selectedBuId,
                tagType: values.tagType,
                tagName: String(values.tagName).trim(),
            });
            if (res && res.status === 200) {
                message.success(res.message || '新增成功');
                closeAddModal();
                setCurrent(1);
                fetchList();
            }
        } catch (e) {
            if (e?.errorFields) {
                return;
            }
            message.error(e?.message || '新增失败');
        } finally {
            setLoading(false);
        }
    };

    const openDeleteModal = record => {
        if (isTagInUse(record)) {
            setErrorModalContent('Tag已被文件/成员使用，不能删除!');
            setErrorModalVisible(true);
            return;
        }
        setDeleteModal({ visible: true, record });
    };

    const openEditModal = record => {
        setEditModal({ visible: true, record });
        editForm.setFieldsValue({
            status: record.status === 1 ? 1 : 0,
        });
    };

    const closeEditModal = () => {
        setEditModal({ visible: false, record: null });
        editForm.resetFields();
    };

    const onConfirmEdit = async () => {
        const record = editModal.record;
        const id = record?.id;
        if (id == null) {
            return;
        }
        try {
            const values = await editForm.validateFields();
            setLoading(true);
            const res = await knowledgeTaTagUpdate({
                id,
                buId: record.buId ?? selectedBuId,
                tagType: record.tagType,
                tagName: record.tagName,
                status: values.status,
            });
            if (res && (res.status === 200 || res.success === true)) {
                message.success(`Tag${values.status === 1 ? '已启用' : '已停用'}，请查看！`);
                closeEditModal();
                fetchList();
            }
        } catch (e) {
            if (e?.errorFields) {
                return;
            }
            message.error(e?.message || '修改失败');
        } finally {
            setLoading(false);
        }
    };

    const closeDeleteModal = () => {
        setDeleteModal({ visible: false, record: null });
    };

    const closeErrorModal = () => {
        setErrorModalVisible(false);
        setErrorModalContent('');
    };

    const onConfirmDelete = async () => {
        const id = deleteModal.record?.id;
        if (id == null) {
            return;
        }
        setLoading(true);
        try {
            const res = await knowledgeTaTagDelete(id);
            if (res && res.status === 200) {
                message.success('删除成功');
                closeDeleteModal();
                fetchList();
            } else {
                setErrorModalContent(res.message || '删除失败');
                setErrorModalVisible(true);
            }
        } catch (e) {
            message.error(e?.message || '删除失败');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'TA Tag类型',
            dataIndex: 'tagType',
            key: 'tagType',
            width: 140,
            ellipsis: true,
        },
        {
            title: 'TA Tag名称',
            dataIndex: 'tagName',
            key: 'tagName',
            width: 160,
            ellipsis: true,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: text => (text === 1 ? '启用' : '停用'),
        },
        {
            title: '文件Tag数量',
            dataIndex: 'fileCnt',
            key: 'fileCnt',
            width: 100,
        },
        {
            title: '成员Tag数量',
            dataIndex: 'memberCnt',
            key: 'memberCnt',
            width: 100,
        },
        {
            title: '创建人',
            dataIndex: 'creatorName',
            key: 'creatorName',
            width: 140,
            ellipsis: true,
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 170,
            render: text => {
                if (!text) {
                    return ' ';
                }
                return dayjs(text).format('YYYY-MM-DD HH:mm');
            },
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            align: 'left',
            render: (_, record) => (
                <div className={styles.actionIcons}>
                    <img
                        src={editIcon}
                        alt="编辑"
                        className={styles.actionIcon}
                        onClick={() => openEditModal(record)}
                    />
                    <img
                        src={DeleteIcon}
                        alt="删除"
                        className={styles.actionIcon}
                        onClick={() => openDeleteModal(record)}
                    />
                </div>
            ),
        },
    ];

    return (
        <>
            <Layout className={layoutStyles.pageLayout}>

                <Content className={`${Gstyle.content} ${layoutStyles.pageContent}`}>
                    <div className={styles.buTabMenuWrap}>
                        <Menu
                            mode="horizontal"
                            className={styles.buTabMenu}
                            triggerSubMenuAction="click"
                            selectedKeys={selectedBuId != null ? [String(selectedBuId)] : []}
                            onClick={onBuTabChange}
                        >
                            {buTabs.map(tab => (
                                <Menu.Item
                                    key={String(tab.value)}
                                    className={`${styles.meunitem} ${String(selectedBuId) !== String(tab.value)
                                        ? styles.meunactiv
                                        : ''
                                        }`}
                                >
                                    {tab.label}
                                </Menu.Item>
                            ))}
                        </Menu>
                    </div>
                    <div className={styles.pageHeader}>
                        <div className={styles.pageTitle}>TA Tag列表</div>
                        <Button type="primary" onClick={openAddModal}>
                            Tag新增
                        </Button>
                    </div>
                    <div ref={tableWrapRef} className={layoutStyles.tableWrap}>
                        <Table
                            className={Gstyle.table}
                            loading={loading}
                            columns={columns}
                            dataSource={dataSource}
                            rowKey={record => record.id}
                            scroll={tableScrollY != null ? { y: tableScrollY } : undefined}
                            pagination={{
                                total,
                                pageSize,
                                current,
                                showTotal: t => `共 ${t} 条`,
                                showQuickJumper: true,
                                showSizeChanger: true,
                                onChange: (page, size) => {
                                    setCurrent(page);
                                    setPageSize(size);
                                },
                                onShowSizeChange: (_page, size) => {
                                    setCurrent(1);
                                    setPageSize(size);
                                },
                            }}
                            rowClassName={(record, index) => {
                                const isMultipleOfTwo = (index + 1) % 2 === 0;
                                return isMultipleOfTwo ? Gstyle.tableRow : '';
                            }}
                        />
                    </div>
                </Content>
            </Layout>

            <Modal
                centered
                title={
                    <div className={Gstyle.modalTitle}>
                        <span>Tag新增</span>
                    </div>
                }
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={addModalVisible}
                onCancel={closeAddModal}
                width={410}
                destroyOnClose
                footer={
                    <div className={Gstyle.custommodalfooter}>
                        <Button className={Gstyle.mythemebutton} ghost onClick={closeAddModal}>
                            取消
                        </Button>
                        <Button type="primary" loading={loading} onClick={onConfirmAdd}>
                            确定
                        </Button>
                    </div>
                }
            >
                <Form
                    form={addForm}
                    layout="horizontal"
                    className={styles.addFormLabel}
                    requiredMark
                >
                    <Form.Item
                        label="Tag类型"
                        name="tagType"
                        rules={[{ required: true, message: '请选择 Tag类型' }]}
                    >
                        <Select
                            placeholder="请选择Tag类型"
                            options={taTypeOptions}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Tag名称"
                        name="tagName"
                        rules={[{ required: true, message: '请输入 Tag名称' }]}
                    >
                        <Input placeholder="请输入Tag名称" allowClear />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                centered
                title={
                    <div className={Gstyle.modalTitle}>
                        <span>Tag状态编辑</span>
                    </div>
                }
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={editModal.visible}
                onCancel={closeEditModal}
                width={410}
                destroyOnClose
                footer={
                    <div className={Gstyle.custommodalfooter}>
                        <Button className={Gstyle.mythemebutton} ghost onClick={closeEditModal}>
                            取消
                        </Button>
                        <Button type="primary" loading={loading} onClick={onConfirmEdit}>
                            确定
                        </Button>
                    </div>
                }
            >
                <Form
                    form={editForm}
                    layout="horizontal"
                    className={styles.addFormLabel}
                    requiredMark
                >
                    <Form.Item label="Tag类型">
                        <span>{editModal.record?.tagType || ' '}</span>
                    </Form.Item>
                    <Form.Item label="Tag名称">
                        <span>{editModal.record?.tagName || ' '}</span>
                    </Form.Item>
                    <Form.Item
                        label="Tag状态"
                        name="status"
                        rules={[{ required: true, message: '请选择 Tag状态' }]}
                    >
                        <Select
                            placeholder="请选择"
                            options={[
                                { label: '启用', value: 1 },
                                { label: '停用', value: 0 },
                            ]}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                centered
                title={
                    <div className={Gstyle.modalTitle}>
                        <span>确认删除</span>
                    </div>
                }
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={deleteModal.visible}
                onCancel={closeDeleteModal}
                width={410}
                footer={
                    <div className={Gstyle.custommodalfooter}>
                        <Button className={Gstyle.mythemebutton} ghost onClick={closeDeleteModal}>
                            否
                        </Button>
                        <Button type="primary" loading={loading} onClick={onConfirmDelete}>
                            是
                        </Button>
                    </div>
                }
            >
                <p className={Gstyle.errorModalContent}>是否确认删除？</p>
            </Modal>
            <Modal
                centered
                zIndex={1001}
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={errorModalVisible}
                onCancel={closeErrorModal}
                width={410}
                footer={
                    <div style={{ textAlign: 'center' }}>
                        <Button
                            type="primary"
                            onClick={closeErrorModal}
                            style={{
                                width: '170px',
                                height: '40px',
                                borderRadius: '6px',
                            }}
                        >
                            确定
                        </Button>
                    </div>
                }
            >
                <p className={Gstyle.errorModalContent} style={{ marginTop: '30px' }}>
                    {errorModalContent}
                </p>
            </Modal>
        </>
    );
};

export default TaTagManagement;
